import { type FC, useLayoutEffect, useState } from "react"

import dayjs from "@forento/core/utilities/dayjs"
import { toNullIfEmpty } from "@forento/core/utilities/string"
import { getBrowserTimeZone } from "@forento/core/utilities/timeZone"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import ExternalBuyLinkFields from "~/components/ExternalBuyLinkFields"
import ImageUploader from "~/components/ImageUploader"
import InputField, { InputTextArea, useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import { DefaultModal, ModalButtons, ModalHeader, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ZapierId from "~/components/ZapierId"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import useExternalBuyLink from "~/hooks/useExternalBuyLink"
import usePrice from "~/hooks/usePrice"
import { query } from "~/utilities/trpc"

type Props = { eventId: number; isOpen: boolean; onClose(): void; onReload(): Promise<unknown> }
const EditModal: FC<Props> = ({ eventId, isOpen, onClose, onReload }) => {
	const alert = useAlert()
	const toast = useToast()
	const platform = usePlatform()!

	const enabled = isOpen

	const { data: event, error } = query.event.get.useQuery(eventId, { enabled, refetchOnWindowFocus: false })
	const memberships = query.membership.list.useQuery(undefined, { enabled })

	const [title, setTitle] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const longDescription = useRichTextArea({ label: "Description", initialValue: event?.longDescription })
	const [startDate, setStartDate] = useState(dayjs())
	const [endDate, setEndDate] = useState(dayjs())
	const [isPaid, setPaid] = useState(false)
	const { price, setPrice, priceValue } = usePrice()
	const [isManualOnly, setManualOnly] = useState(false)
	const externalBuyLink = useExternalBuyLink(null)

	// A downgraded platform keeps the option so an already manual-only event can be switched away from it.
	const canSelectManualOnly = platform.plan.access.assignProducts || event?.isManualOnly === true
	const [location, setLocation] = useState("")
	const [videoCallLink, setVideoCallLink] = useState("")
	const [membershipIds, setMembershipIds] = useState<number[]>([])

	const thumbnail = useFile(eventId, event?.thumbnailFilePath ?? null)

	const save = query.event.update.useMutation({
		async onSuccess() {
			await onReload()
			onClose()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to save event. Please try again.")
		},
	})

	const isInputValid = title.trim().length > 0

	useLayoutEffect(() => {
		if (event == null || eventId === null) return
		if (event.id !== eventId) return

		const abortController = new AbortController()

		setTitle(event.title)
		setShortDescription(event.shortDescription ?? "")
		setStartDate(dayjs(event.startDate))
		setEndDate(dayjs(event.endDate))
		setPaid(event.price !== null)
		setPrice(event.price?.amount.toString() ?? "")
		setManualOnly(event.isManualOnly)
		externalBuyLink.reset(event.externalBuyLink)
		setLocation(event.location ?? "")
		setVideoCallLink(event.videoCallLink ?? "")
		setMembershipIds(event.membershipIds)

		return () => {
			abortController.abort()
		}
	}, [eventId, event, externalBuyLink.reset, setPrice])

	return (
		<DefaultModal
			onClose={onClose}
			width={800}
			isOpen={isOpen}
			onSubmit={async () => {
				if (!isInputValid || eventId === null || event == null) return
				if (startDate > endDate) {
					await alert.show("Invalid date", "The start date must be before the end date.")
					return
				}
				if (isManualOnly && externalBuyLink.error !== null) {
					await alert.show("Error", externalBuyLink.error)
					return
				}
				save.mutate({
					id: eventId,
					data: {
						title,
						shortDescription,
						longDescription: await longDescription.exportEditorState(),
						thumbnailDataUrl: await thumbnail.exportToDataUrl(),
						startDate: startDate.toDate(),
						endDate: endDate.toDate(),
						timeZone: event.timeZone ?? getBrowserTimeZone(),
						price: isManualOnly || !isPaid ? null : priceValue,
						isManualOnly,
						externalBuyLink: isManualOnly ? externalBuyLink.value : null,
						location: toNullIfEmpty(location.trim()) ?? null,
						videoCallLink: toNullIfEmpty(videoCallLink.trim()) ?? null,
						membershipIds: isManualOnly ? [] : membershipIds,
					},
				})
			}}
		>
			<ModalHeader className="flex-wrap gap-3">
				<ModalTitle>Edit event</ModalTitle>
				<ZapierId id={eventId} />
			</ModalHeader>
			{error || memberships.error ? (
				<>
					<ModalText>Failed to load event.</ModalText>
					<ModalButtons>
						<Button variant="secondary" onClick={onClose}>
							Close
						</Button>
					</ModalButtons>
				</>
			) : !event || !memberships.data ? (
				<>
					<PartialLoadingPage />
					<ModalButtons>
						<Button variant="secondary" onClick={onClose}>
							Close
						</Button>
					</ModalButtons>
				</>
			) : (
				<>
					<ModalInputs>
						<InputField label="Title *" value={title} onChange={setTitle} />
						<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
						{longDescription.element}
						<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
						<InputField
							inputType="datetime-local"
							label="Start date *"
							value={startDate.format("YYYY-MM-DD HH:mm")}
							onChange={value => setStartDate(dayjs(value))}
						/>
						<InputField
							inputType="datetime-local"
							label="End date *"
							value={endDate.format("YYYY-MM-DD HH:mm")}
							onChange={value => setEndDate(dayjs(value))}
						/>
						<div>
							<Checkbox label="Is paid" isChecked={isPaid} onChange={setPaid} isDisabled={isManualOnly} />
							<InputField
								label="Price"
								value={price.toString()}
								inputType="number"
								onChange={setPrice}
								disabled={!isPaid || isManualOnly}
							/>
						</div>
						{canSelectManualOnly && (
							<Checkbox label="Manual only" isChecked={isManualOnly} onChange={setManualOnly} />
						)}
						{isManualOnly && <ExternalBuyLinkFields type="event" link={externalBuyLink} />}
						<InputTextArea label="Location" value={location} onChange={setLocation} />
						<InputField label="Video call link" value={videoCallLink} onChange={setVideoCallLink} />
						{memberships.data.length > 0 && (
							<div>
								<InputLabel>Include in memberships</InputLabel>
								{memberships.data.map(membership => (
									<Checkbox
										key={membership.id}
										label={membership.title}
										isChecked={membershipIds.includes(membership.id)}
										onChange={() => toggleStateArray(setMembershipIds, membership.id)}
										isDisabled={isManualOnly}
									/>
								))}
							</div>
						)}
					</ModalInputs>
					<ModalButtons>
						<Button variant="secondary" onClick={onClose} isDisabled={save.isPending}>
							Cancel
						</Button>
						<SubmitButton variant="primary" isLoading={save.isPending} isDisabled={!isInputValid}>
							Save event
						</SubmitButton>
					</ModalButtons>
				</>
			)}
		</DefaultModal>
	)
}

export default EditModal
